/**
 * ApplicationPicker — searchable application link for the work-report
 * form (C3).
 *
 * Fourth thin wrapper over the shared ``AsyncCombobox`` (C0). It owns
 * only the application vocabulary and delegates debounce, paging,
 * stale-response protection, keyboard and theming to the base.
 *
 * Replaces a native <select> filled with ``fetchAdminApplications({limit:
 * 500})`` whose option text was the applicant's name alone — with several
 * applications per listing, two rows for the same person differ only in
 * status and object, neither of which was visible.
 *
 * Rendered shape:
 *   Maria Santos
 *   maria@example.test · In Prüfung · Madrid / CL-004
 *
 * Server contract:
 *   GET /api/admin/applications/picker-options?q=…&limit&offset
 *   GET /api/admin/applications/{id}   for hydrating the current link
 * The picker route reuses the list route's item shape and its batched
 * listing/unit/room enrichment, so nothing here costs a query per row.
 *
 * The payload it feeds back is unchanged: a bare ``application_id``.
 */
import React from "react";

import AsyncCombobox from "../../../../components/ui/AsyncCombobox";
import {
  fetchAdminApplication,
  fetchAdminApplicationsPage,
} from "../../../../api/adminData";

const PAGE_SIZE = 20;

/** Mirrors ApplicationStatusLiteral (routes_admin_applications.py:59-67).
 * Falls back to the raw value so an unmapped status stays legible. */
const STATUS_LABEL = {
  new: "Neu",
  reviewing: "In Prüfung",
  viewing_scheduled: "Besichtigung geplant",
  accepted: "Angenommen",
  rejected: "Abgelehnt",
  archived: "Archiviert",
  converted: "Zu Mieter konvertiert",
};

export function statusLabel(application) {
  const raw = application?.status || null;
  if (!raw) return null;
  return STATUS_LABEL[raw] || raw;
}

/**
 * "Maria Santos", the company for a corporate applicant, then e-mail,
 * then an honest placeholder. Never a UUID.
 * Exported for tests.
 */
export function applicantName(application) {
  const person = [application?.first_name, application?.last_name]
    .map((p) => (p || "").trim())
    .filter(Boolean)
    .join(" ");
  if (person) return person;
  const company = (application?.company_name || "").trim();
  if (company) return company;
  const email = (application?.email || "").trim();
  if (email) return email;
  return "Bewerbung ohne Namen";
}

/**
 * "Madrid / CL-004", "Madrid", "CL-004" or null. Prefers the listing
 * title because that is what the applicant actually applied to; unit and
 * room are the concrete object behind it.
 * Exported for tests.
 */
export function objectLabel(application) {
  const listing = (application?.listing_title || "").trim();
  if (listing) return listing;
  return (
    [
      (application?.unit_short_id || "").trim(),
      (application?.room_name || "").trim(),
    ]
      .filter(Boolean)
      .join(" / ") || null
  );
}

/** Secondary line: "E-Mail · Status · Objekt". Segments that are missing
 * are dropped rather than rendered as an empty separator.
 * Exported for tests. */
export function secondaryLine(application) {
  return [
    (application?.email || "").trim() || null,
    statusLabel(application),
    objectLabel(application),
  ]
    .filter(Boolean)
    .join(" · ");
}

function isConcluded(application) {
  const s = application?.status;
  return s === "rejected" || s === "archived" || s === "converted";
}

export default function ApplicationPicker({
  value,
  onChange,
  disabled = false,
}) {
  const [selected, setSelected] = React.useState(null);

  // Hydrate the CURRENT link by id (edit mode, and C1.1 prefill from a
  // task). A concluded application — or one past page 1 — would
  // otherwise render as "nothing selected" and a save could drop it.
  React.useEffect(() => {
    if (!value) {
      setSelected(null);
      return undefined;
    }
    if (selected && String(selected.id) === String(value)) return undefined;
    let cancelled = false;
    fetchAdminApplication(value)
      .then((application) => {
        if (!cancelled && application) setSelected(application);
      })
      .catch(() => {
        // A deleted or inaccessible application must not break the form.
      });
    return () => {
      cancelled = true;
    };
  }, [value, selected]);

  const fetchPage = React.useCallback(
    ({ query, offset, limit }) =>
      fetchAdminApplicationsPage({ q: query || undefined, limit, offset }),
    []
  );

  const handleChange = React.useCallback(
    (id, item) => {
      setSelected(item);
      onChange(id);
    },
    [onChange]
  );

  return (
    <AsyncCombobox
      label="Bewerbung (optional)"
      placeholder="— keine —"
      searchPlaceholder="Bewerbung nach Name, Firma oder E-Mail suchen …"
      loadingText="Bewerbungen werden geladen …"
      emptyText="Keine passenden Bewerbungen gefunden."
      errorText="Bewerbungen konnten nicht geladen werden."
      value={value}
      selectedItem={selected}
      onChange={handleChange}
      fetchPage={fetchPage}
      getItemId={(a) => a.id}
      getItemLabel={(a) => applicantName(a)}
      pageSize={PAGE_SIZE}
      disabled={disabled}
      renderItem={(application) => (
        <span className="block min-w-0">
          <span
            title={applicantName(application)}
            className={
              "block truncate text-[13px] " +
              (isConcluded(application)
                ? "font-normal text-muted-foreground"
                : "font-medium text-foreground")
            }
          >
            {applicantName(application)}
          </span>
          <span
            title={secondaryLine(application)}
            className="block truncate text-[11px] text-muted-foreground"
          >
            {secondaryLine(application)}
          </span>
        </span>
      )}
    />
  );
}
